import type { Route } from './+types/appraisal';
import { AppraisalWizard } from '@/components/appraisal/appraisal-wizard';
import { config } from '@/config.server';
import { appraisalTypes, contactMethods, contactTimes, propertyTypes, sellingReasons, timeframes } from '@/data/appraisal';
import type { AppraisalPayload } from '@/features/appraisal/schema';
import { appraisalPayloadSchema } from '@/features/appraisal/schema';
import { businessOwnerRecipients, sendTransmission } from '@/features/email/transmission.server';
import { ServicesFactory } from '@/services/servicesFactory';
import { inheritRootLdJson } from '@/utils/schema';

/** The agency's sole agent today; promote to config if a second agent joins. */
const AGENT_FIRST_NAME = 'Lucio';

/** Looks up the display name for an option id, falling back to the id itself if unmatched. */
function labelFor(options: Array<{ id: string; name: string }>, id: string): string {
  return options.find((option) => option.id === id)?.name ?? id;
}

/**
 * Maps a validated payload to the substitution data shared by both e-mail templates
 * (`emails/appraisal-customer-confirmation.json` and `emails/appraisal-agent-notification.json`
 * — keys here must match those templates' `{{merge_fields}}` exactly since they're already
 * uploaded to Stack9 under fixed template_ids).
 */
function buildSubstitutionData(payload: AppraisalPayload) {
  const contactFullName = `${payload.contact.firstName} ${payload.contact.lastName}`;
  const photoCount = payload.photos?.length ?? 0;

  return {
    agent_first_name: AGENT_FIRST_NAME,
    current_year: new Date().getFullYear(),
    submitted_at: new Date().toLocaleString('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Australia/Brisbane',
    }),
    property_address: payload.property.address,
    property_type_label: labelFor(propertyTypes, payload.property.propertyType),
    bedrooms: payload.property.bedrooms,
    bathrooms: payload.property.bathrooms,
    car_spaces: payload.property.carSpaces,
    land_size_label: payload.property.landSizeSqm ? `${payload.property.landSizeSqm} sqm` : 'Not provided',
    reason_label: labelFor(sellingReasons, payload.timeline.reason),
    appraisal_type_label: labelFor(appraisalTypes, payload.timeline.appraisalType),
    timeframe_label: labelFor(timeframes, payload.timeline.timeframe),
    notes_label: payload.notes?.trim() || 'No additional notes provided.',
    photo_count_label: photoCount > 0 ? `${photoCount} photo${photoCount === 1 ? '' : 's'} attached` : 'No photos attached',
    contact_full_name: contactFullName,
    contact_first_name: payload.contact.firstName,
    contact_email: payload.contact.email,
    contact_phone: payload.contact.phone,
    contact_phone_href: payload.contact.phone.replace(/[^\d+]/g, ''),
    preferred_method_label: labelFor(contactMethods, payload.contact.preferredMethod),
    preferred_time_label: labelFor(contactTimes, payload.contact.preferredTime),
  };
}

export function meta({ matches }: Route.MetaArgs) {
  return [
    ...inheritRootLdJson(matches),
    { title: 'Free Property Appraisal | Meet Real Estate — Kingaroy & South Burnett' },
    {
      name: 'description',
      content:
        "Request a free, no-obligation property appraisal from Meet Real Estate. Tell us about your property and Lucio will be in touch.",
    },
  ];
}

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const raw = String(formData.get('payload') ?? '');

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'invalidData' as const };
  }

  const result = appraisalPayloadSchema.safeParse(parsed);
  if (!result.success) {
    return { ok: false, error: 'invalidData' as const };
  }

  const { logger } = ServicesFactory.forAnonymous(request);
  const substitutionData = buildSubstitutionData(result.data);

  const notificationTemplateId = config.environment.APP.EMAIL.TEMPLATE_NOTIFICATION_ID;
  const ownerRecipients = businessOwnerRecipients();
  if (!notificationTemplateId || ownerRecipients.length === 0) {
    logger.error('appraisal.submit.emailNotConfigured', {
      hasTemplateId: Boolean(notificationTemplateId),
      ownerRecipientCount: ownerRecipients.length,
    });
    return { ok: false, error: 'submissionFailed' as const };
  }

  try {
    // Owner notification is the actual lead capture — it must succeed for the
    // submission to count.
    await sendTransmission(notificationTemplateId, ownerRecipients, substitutionData);
  } catch (error) {
    logger.error('appraisal.submit.notificationFailed', { reason: String(error) });
    return { ok: false, error: 'submissionFailed' as const };
  }

  // Customer confirmation is a nice-to-have — don't fail the submission over it.
  const confirmationTemplateId = config.environment.APP.EMAIL.TEMPLATE_CONFIRMATION_ID;
  if (confirmationTemplateId) {
    void sendTransmission(
      confirmationTemplateId,
      [{ email: result.data.contact.email, name: substitutionData.contact_full_name }],
      substitutionData,
    ).catch(() => {}); // failures are already logged inside sendTransmission
  }

  // TODO(marketing-email): once we know which platform subscriber sign-up
  // goes through, hand it result.data.contact.{firstName,lastName,email} here.

  return { ok: true as const };
}

export default function Appraisal() {
  return (
    <main>
      <section className="section section--light pt-28 sm:pt-32 lg:pt-40">
        <div className="container container--narrow">
          <AppraisalWizard />
        </div>
      </section>
    </main>
  );
}
